/**
 * The two weights a picture is sent in (D233, `wire/src/photo.rs`).
 *
 * **WebP on both sides.** The desktop writes it with libwebp and this side
 * writes it with the browser's own encoder, so the two clients no longer
 * merely agree on the ceilings — they agree on the bytes. That is the whole
 * reason the photo path moved off AVIF: a browser will not expose an AVIF
 * encoder to a canvas, and `canvas.toBlob("image/webp")` has been there since
 * Chrome 32, Firefox 96 and Safari 14.
 *
 * WebP has alpha, which JPEG (what this file used to write) did not, so a
 * transparent PNG now survives as itself instead of being flattened onto
 * white. The one browser worth worrying about is an old Safari, where
 * `toBlob` quietly hands back a PNG under the name it was asked for; that is
 * what {@link photoWeightsSupported} checks for, and a client that fails it
 * falls back to uploading the original.
 *
 * The numbers below mirror `echo_wire::photo`. They are not generated: ts-rs
 * emits types, not constants, and `MAX_ATTACHMENTS` is already mirrored by
 * hand a few files over. `photo.test.ts` pins them.
 */

import { STILL_MAX_BYTES, STILL_MAX_SIDE, STILL_MIN_SIDE } from "./stickers";

/** Long side of the thumbnail, and the ceiling it has to fit. */
export const THUMB_DIM = 128;
export const THUMB_MAX_BYTES = 15 * 1024;
/** Long side of the preview — the weight a reader actually looks at. */
export const PREVIEW_DIM = 1024;
export const PREVIEW_MAX_BYTES = 300 * 1024;
/** An untouched original, which only travels when it is sent as a file. */
export const ORIGINAL_MAX_BYTES = 5 * 1024 * 1024;

/** A face — the account's own, or a pet's (pets-plan.md D352) — checked
 * before a byte moves (D61/D63): the gateway looks only when it is attached,
 * after the bytes and the quota are spent. */
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

/** webp / jpeg / png / avif by their leading bytes — the same check the
 * desktop's profile::check_avatar does before a byte moves. AVIF is still on
 * the list although nothing writes one any more: it was our own format until
 * the photo path moved to WebP, so a picture saved out of an older channel has
 * to be pickable again. */
export function looksLikeImage(head: Uint8Array): boolean {
  if (head.length >= 3 && head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return true; // jpeg
  if (
    head.length >= 8 &&
    head[0] === 0x89 &&
    head[1] === 0x50 &&
    head[2] === 0x4e &&
    head[3] === 0x47
  )
    return true; // png
  if (head.length >= 12) {
    if (
      String.fromCharCode(...head.slice(0, 4)) === "RIFF" &&
      String.fromCharCode(...head.slice(8, 12)) === "WEBP"
    )
      return true; // webp
    const brand = String.fromCharCode(...head.slice(4, 12));
    if (brand.startsWith("ftypavif") || brand.startsWith("ftypavis")) return true; // avif
  }
  return false;
}

/**
 * How an encode gives ground when it misses its ceiling: quality first, then
 * geometry — the same shape and the same rungs as the core's ladder, in the
 * canvas API's 0..1 quality rather than the encoder's 0..100. Resolution is
 * what a reader notices, so 0.68 of 1024 px beats 0.82 of 512 px at the same
 * weight.
 */
export const LADDER: ReadonlyArray<readonly [eighths: number, quality: number]> = [
  [8, 0.82],
  [8, 0.68],
  [8, 0.55],
  [6, 0.55],
  [4, 0.52],
  [2, 0.5],
  [1, 0.45],
];

/** Below this a picture is a swatch; the ladder stops and takes what it has. */
export const MIN_LADDER_DIM = 64;

export interface Weighed {
  blob: Blob;
  width: number;
  height: number;
}

export interface PhotoWeights {
  /** 1024 px, ≤ 300 KiB — the `file_id` of the attachment. */
  preview: Weighed;
  /** 128 px, ≤ 15 KiB — its `thumb_file_id`. */
  thumb: Weighed;
}

/**
 * Whether this browser can produce the weights at all: it has to decode the
 * original off the main thread *and* actually encode WebP.
 *
 * The second half is not paranoia. `toDataURL`/`toBlob` are specified to fall
 * back to PNG for a type they do not support, silently and under the requested
 * name — so a Safari 13 would upload 1024 px of lossless PNG as a "preview",
 * miss 300 KiB by a mile, and walk the whole ladder down to a swatch before
 * giving up. Better to send the original.
 */
export function photoWeightsSupported(): boolean {
  return typeof createImageBitmap === "function" && webpSupported();
}

/** Memoised: it costs a 1x1 canvas, and the answer cannot change. */
let webpEncodes: boolean | null = null;

function webpSupported(): boolean {
  if (webpEncodes === null) {
    try {
      const probe = document.createElement("canvas");
      probe.width = 1;
      probe.height = 1;
      webpEncodes = probe.toDataURL("image/webp").startsWith("data:image/webp");
    } catch {
      webpEncodes = false;
    }
  }
  return webpEncodes;
}

/**
 * Both weights of one picture, from one decode of the original.
 *
 * Throws if the browser cannot decode the file. Callers ask
 * {@link photoWeightsSupported} first and fall back to uploading the original
 * under `kind=photo` — the pre-D233 behaviour, and still a valid attachment:
 * the gateway reads a photo's geometry from its header and takes JPEG and PNG
 * for exactly this reason. What such a client loses is the thumbnail.
 */
export async function photoWeights(file: Blob): Promise<PhotoWeights> {
  const bitmap = await createImageBitmap(file);
  try {
    const preview = await encodeWithin(bitmap, PREVIEW_DIM, PREVIEW_MAX_BYTES);
    const thumb = await encodeWithin(bitmap, THUMB_DIM, THUMB_MAX_BYTES);
    return { preview, thumb };
  } finally {
    bitmap.close();
  }
}

/**
 * A still sticker out of a picture (docs/stickers-plan.md D564): the long side
 * down to 512, WebP with its alpha down the ladder until it fits 128 KiB — or,
 * where the browser encodes no WebP, PNG with its long side stepped down
 * (`STILL_PNG_SIDES`), which the blob's type says. A picture that already is a
 * still sticker goes up as it is (`readyStill`). Never larger than the
 * picture: one under 128 px stays under it, and the editor says so. Throws
 * for a picture past `SOURCE_MAX_PIXELS` by its header, before it is decoded.
 * The desktop's `media::make_still_sticker`.
 */
export async function stickerImage(file: Blob): Promise<Weighed> {
  // A file small enough to be a sticker already is read whole; of any other,
  // the head, which says how large it decodes.
  const read = new Uint8Array(await file.slice(0, STILL_MAX_BYTES).arrayBuffer());
  const ready = file.size <= STILL_MAX_BYTES ? readyStill(read) : null;
  if (ready !== null) {
    // The header and the chunks say what a file is, not that it decodes: a
    // truncated picture would go up as it is and stand broken in every grid.
    // Cheap at 512 px, and a refusal reads as the file not fitting.
    (await createImageBitmap(file)).close();
    return {
      blob: new Blob([read], { type: ready.mime }),
      width: ready.width,
      height: ready.height,
    };
  }
  const head = pictureHead(read);
  if (head !== null && head.width * head.height > SOURCE_MAX_PIXELS) {
    throw new Error("the picture is larger than a sticker is made from");
  }
  const bitmap = await createImageBitmap(file);
  try {
    if (webpSupported()) return await encodeWithin(bitmap, STILL_MAX_SIDE, STILL_MAX_BYTES);
    return await pngWithin(bitmap);
  } finally {
    bitmap.close();
  }
}

/** The long sides a still sticker steps down through as PNG, where the browser
 * encodes no WebP (every Safari): PNG has no quality to give, so the picture
 * gives ground — 64 px a rung, from the largest a still sticker may be to the
 * smallest (D564). A photograph is 220–350 KiB of PNG at 512 px. */
export const STILL_PNG_SIDES: readonly number[] = [512, 448, 384, 320, 256, 192, 128];

/** The sizes a picture of `width`×`height` is tried at as a PNG still
 * sticker, in order: its size at each rung of `STILL_PNG_SIDES`, each size
 * once — a picture smaller than a rung is drawn at its own size, never up.
 * Exported for its own test, as `fitWithin` is. */
export function stillSides(
  width: number,
  height: number,
): Array<{ width: number; height: number }> {
  const sides: Array<{ width: number; height: number }> = [];
  for (const side of STILL_PNG_SIDES) {
    const size = fitWithin(width, height, side);
    const last = sides[sides.length - 1];
    if (last === undefined || last.width !== size.width || last.height !== size.height) {
      sides.push(size);
    }
  }
  return sides;
}

/** The largest PNG of `bitmap` inside 128 KiB, down `stillSides` — or the
 * smallest rung's when none fits, which the editor refuses in words. */
async function pngWithin(bitmap: ImageBitmap): Promise<Weighed> {
  let best: Weighed | null = null;
  for (const { width, height } of stillSides(bitmap.width, bitmap.height)) {
    const blob = await toPng(draw(bitmap, width, height));
    best = { blob, width, height };
    if (blob.size <= STILL_MAX_BYTES) break;
  }
  if (!best) throw new Error("the PNG ladder is empty");
  return best;
}

// ---------------------------------------------------------------------------
// A picture's header, read before anything is decoded — the gateway's own
// reading (`gateway/src/chat/sniff.rs`), so what the editor sends as it is
// is what the gateway takes (D564).

/** What a picture's header says: its kind, by its signature, and its size. */
export interface PictureHead {
  mime: "image/png" | "image/webp" | "image/jpeg";
  width: number;
  height: number;
}

/** The most pixels a picture is decoded at to become a still sticker: the
 * surface the desktop's decoder holds itself to (8192² — 256 MiB of RGBA,
 * `echo_core::media::image::MAX_DECODE_DIM`), as an area, so a 50 Mpx camera
 * file still fits. A header that says more is a decompression bomb, not a
 * picture: a tab that decoded it would go down with it. */
export const SOURCE_MAX_PIXELS = 8192 * 8192;

/** The bytes `count` long at `at`, as Latin-1 — a chunk's or a box's name. */
function ascii(b: Uint8Array, at: number, count: number): string {
  return String.fromCharCode(...b.subarray(at, at + count));
}

function viewOf(b: Uint8Array): DataView {
  return new DataView(b.buffer, b.byteOffset, b.byteLength);
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/** The kind and size `b`'s header says, or null: no picture of these three,
 * or a header the bytes given end before — a JPEG whose frame header comes
 * after a fat EXIF segment among them. */
export function pictureHead(b: Uint8Array): PictureHead | null {
  const view = viewOf(b);
  if (b.length >= 24 && PNG_SIGNATURE.every((byte, i) => b[i] === byte)) {
    if (ascii(b, 12, 4) !== "IHDR") return null;
    return { mime: "image/png", width: view.getUint32(16), height: view.getUint32(20) };
  }
  if (b.length >= 12 && ascii(b, 0, 4) === "RIFF" && ascii(b, 8, 4) === "WEBP") {
    const size = webpSize(b, view);
    return size === null ? null : { mime: "image/webp", ...size };
  }
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) {
    const size = jpegSize(b, view);
    return size === null ? null : { mime: "image/jpeg", ...size };
  }
  return null;
}

/** A WebP's canvas, from whichever of its three shapes it is: all three keep
 * the size in the first chunk — stored minus one, but in `VP8 `. */
function webpSize(b: Uint8Array, view: DataView): { width: number; height: number } | null {
  switch (ascii(b, 12, 4)) {
    case "VP8 ":
      // The key frame's tag, its start code, then two 16-bit fields whose top
      // two bits are an upscaling hint.
      if (b.length < 30 || b[23] !== 0x9d || b[24] !== 0x01 || b[25] !== 0x2a) return null;
      return {
        width: view.getUint16(26, true) & 0x3fff,
        height: view.getUint16(28, true) & 0x3fff,
      };
    case "VP8L": {
      if (b.length < 25 || b[20] !== 0x2f) return null;
      const bits = view.getUint32(21, true);
      return { width: (bits & 0x3fff) + 1, height: ((bits >>> 14) & 0x3fff) + 1 };
    }
    case "VP8X":
      // A byte of flags, three reserved, then two 24-bit canvas fields.
      if (b.length < 30) return null;
      return {
        width: (view.getUint16(24, true) | (view.getUint8(26) << 16)) + 1,
        height: (view.getUint8(27) | (view.getUint16(28, true) << 8)) + 1,
      };
    default:
      return null;
  }
}

/** A JPEG's size, from its first frame header (SOF0..SOF15 but the three that
 * are not), walking the segments before it. */
function jpegSize(b: Uint8Array, view: DataView): { width: number; height: number } | null {
  let i = 2;
  while (i + 9 < b.length) {
    if (b[i] !== 0xff) {
      // Padding between segments is legal: resynchronise.
      i += 1;
      continue;
    }
    const marker = b[i + 1] ?? 0;
    // Standalone markers carry no length.
    if (
      marker === 0xd8 ||
      marker === 0xd9 ||
      marker === 0x01 ||
      (marker >= 0xd0 && marker <= 0xd7)
    ) {
      i += 2;
      continue;
    }
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      return { width: view.getUint16(i + 7), height: view.getUint16(i + 5) };
    }
    // The start of the scan: no header is left to read.
    if (marker === 0xda) return null;
    i += 2 + Math.max(2, view.getUint16(i + 2));
  }
  return null;
}

/** A PNG's chunks in order — after the signature, a big-endian length, the
 * type, the data and a CRC32 each — as far as `b` holds them, and where the
 * last of them ends. */
function pngChunks(b: Uint8Array, view: DataView): { names: string[]; end: number } {
  const names: string[] = [];
  let at = 8;
  while (at + 8 <= b.length) {
    names.push(ascii(b, at + 4, 4));
    at += 12 + view.getUint32(at);
  }
  return { names, end: at };
}

/** A WebP's chunks in order after its 12-byte header — a FourCC, a
 * little-endian size, the payload and a pad byte to an even length each. */
function webpChunks(b: Uint8Array, view: DataView): string[] {
  const names: string[] = [];
  let at = 12;
  while (at + 8 <= b.length) {
    names.push(ascii(b, at, 4));
    const size = view.getUint32(at + 4, true);
    at += 8 + size + (size & 1);
  }
  return names;
}

/** Chunks that say where and when a picture was made, and with what — which a
 * re-encode drops, and a sticker, published to everybody, must not carry:
 * EXIF, text, the last-modified time, XMP, and C2PA's provenance. */
const PNG_METADATA: readonly string[] = ["eXIf", "tEXt", "zTXt", "iTXt", "tIME", "caBX"];
const WEBP_METADATA: readonly string[] = ["EXIF", "XMP ", "C2PA"];

/** What `bytes` — a whole file — is, when it already is a still sticker the
 * gateway takes as it is (D564): a PNG or a WebP inside 128 KiB, 128..512 px
 * on its long side, and still — no `VP8X` animation bit, `ANIM` or `ANMF`
 * chunk, no `acTL` before the first `IDAT` (`chat::sniff::animated`) — and
 * carrying no metadata. Null for anything that has to be made into one. */
export function readyStill(bytes: Uint8Array): PictureHead | null {
  if (bytes.length > STILL_MAX_BYTES) return null;
  const head = pictureHead(bytes);
  if (head === null || head.mime === "image/jpeg") return null;
  const long = Math.max(head.width, head.height);
  if (head.width === 0 || head.height === 0 || long < STILL_MIN_SIDE || long > STILL_MAX_SIDE) {
    return null;
  }
  const view = viewOf(bytes);
  if (head.mime === "image/png") {
    const { names: chunks, end } = pngChunks(bytes, view);
    // Nothing after the image's end: bytes appended there travel with it.
    if (chunks.at(-1) !== "IEND" || end !== bytes.length) return null;
    const frames = chunks.indexOf("acTL");
    const pixels = chunks.indexOf("IDAT");
    if (frames >= 0 && (pixels < 0 || frames < pixels)) return null;
    if (chunks.some((name) => PNG_METADATA.includes(name))) return null;
  } else {
    if (view.getUint32(4, true) + 8 !== bytes.length) return null;
    const chunks = webpChunks(bytes, view);
    if (chunks[0] === "VP8X" && ((bytes[20] ?? 0) & 0x02) !== 0) return null;
    const moving = (name: string) => name === "ANIM" || name === "ANMF";
    if (chunks.some((name) => moving(name) || WEBP_METADATA.includes(name))) return null;
  }
  return head;
}

/** The largest WebP of `bitmap` that fits both ceilings. */
async function encodeWithin(
  bitmap: ImageBitmap,
  maxDim: number,
  maxBytes: number,
): Promise<Weighed> {
  let best: Weighed | null = null;
  let drawnAt = -1;
  let canvas: Canvas | null = null;
  for (const [eighths, quality] of LADDER) {
    const dim = Math.max(1, Math.floor((maxDim * eighths) / 8));
    // Every rung past the first is smaller or cheaper, so one that has fallen
    // under the floor has nothing left to offer.
    if (dim < MIN_LADDER_DIM && best) break;
    const { width, height } = fitWithin(bitmap.width, bitmap.height, dim);
    // Consecutive rungs share a dim (the three quality steps at the top), and
    // the draw is the expensive half — so it happens once per dim.
    if (drawnAt !== dim || !canvas) {
      canvas = draw(bitmap, width, height);
      drawnAt = dim;
    }
    const blob = await toWebp(canvas, quality);
    best = { blob, width, height };
    if (blob.size <= maxBytes) break;
  }
  if (!best) throw new Error("the encode ladder is empty");
  return best;
}

/** `width`/`height` of the picture with its long side capped at `dim`.
 * Exported for its own test: everything around it needs a canvas. */
export function fitWithin(width: number, height: number, dim: number): { width: number; height: number } {
  const long = Math.max(width, height);
  if (long <= dim) return { width, height };
  const scale = dim / long;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

type Canvas = OffscreenCanvas | HTMLCanvasElement;

/**
 * `OffscreenCanvas` where there is one and a detached `<canvas>` where there
 * is not. Neither is attached to the document either way — this runs while the
 * composer is still on screen and must not disturb it.
 */
function draw(bitmap: ImageBitmap, width: number, height: number): Canvas {
  const canvas: Canvas = typeof OffscreenCanvas === "function"
    ? new OffscreenCanvas(width, height)
    : Object.assign(document.createElement("canvas"), { width, height });
  const ctx = canvas.getContext("2d") as
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D
    | null;
  if (!ctx) throw new Error("this browser has no 2d canvas to scale a picture with");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  // No white fill any more: WebP carries alpha, so a transparent PNG stays
  // transparent instead of being flattened the way the JPEG path had to.
  ctx.drawImage(bitmap, 0, 0, width, height);
  return canvas;
}

function toPng(canvas: Canvas): Promise<Blob> {
  if ("convertToBlob" in canvas) return canvas.convertToBlob({ type: "image/png" });
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("the canvas produced no PNG"))),
      "image/png",
    );
  });
}

function toWebp(canvas: Canvas, quality: number): Promise<Blob> {
  if ("convertToBlob" in canvas) {
    return canvas.convertToBlob({ type: "image/webp", quality });
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("the canvas produced no WebP"))),
      "image/webp",
      quality,
    );
  });
}
